import React from 'react';

import { Section, Stack, Alert, UploadFile } from '@gogame/components';
import { FormErrorMessage } from '@gogame/form';
import { AmountFormatter } from '@gogame/intl-ts';

import {
  INVOICES,
  INVOICE_CURRENCY_CODE,
  INVOICE_ATTACHMENT_ID,
  INVOICE_TYPE,
  INVOICE_AMOUNT,
} from 'scenes/FinancialResults/components/FinancialResultForm/constants';
import { useUploadInvoice } from 'scenes/FinancialResults/data/mutations';

import { invoiceSchema } from '../schemas';
import Invoice from './Invoice';

export { invoiceSchema };

const Invoices = ({
  items = [],
  invoices = [],
  name,
  financialResultCurrency,
  ...props
}) => {
  const uploadInvoice = useUploadInvoice();

  const uploadInvoices = (files) => {
    return Promise.all(Array.from(files).map((file) => uploadInvoice(file)));
  };

  const handleAdd = async (_e, filesArray) => {
    if (!filesArray.length) {
      return;
    }

    try {
      const attachments = await uploadInvoices(filesArray);
      props.onAdd(
        attachments.map(({ id, name }) => ({
          name,
          [INVOICE_TYPE]: 0,
          [INVOICE_CURRENCY_CODE]: financialResultCurrency,
          [INVOICE_ATTACHMENT_ID]: id,
          [INVOICE_AMOUNT]: 0,
        }))
      );
      return attachments;
    } catch (e) {
      return [];
    }
  };

  const handleReplaceByIndex = (index) => async (_, filesArray) => {
    if (!filesArray.length) {
      return;
    }

    const formData = filesArray[0];
    const invoice = await uploadInvoice(formData);

    if (invoice) {
      const { id, name } = invoice;
      props.onReplace(index, {
        ...items[index],
        [INVOICE_ATTACHMENT_ID]: id,
        name,
        id: undefined,
      });
    }
  };

  const handleRemoveByIndex = (index) => () => {
    props.onRemove(index);
  };

  return (
    <Section
      title={'Invoices'}
      subTitle={<FormErrorMessage name={INVOICES} />}
      disableSubTitleTypography={true}
    >
      <Stack spacing={2}>
        <UploadFile
          dataCy={'attachInvoices'}
          name={name}
          label={'Select invoices'}
          tooltip={'.pdf, .png, .jpg, .jpeg, .doc, .docx, .xls, .xlsx'}
          accept={
            '.pdf, image/png,  image/jpg, image/jpeg, .doc, .docx, .xls, .xlsx'
          }
          onUpload={handleAdd}
          variant={'outlined'}
          fileLabels={false}
          multiple={true}
          disabled={props.attachDisabled}
        />
        {items.map((item, i) => {
          const { amount, actualSpend } = invoices[i] || {};
          return (
            <Invoice
              {...item}
              {...amount}
              actualSpend={actualSpend || {}}
              loading={props.loading}
              key={i}
              error={props.error}
              id={`${INVOICES}[${i}]`}
              onReplace={handleReplaceByIndex(i)}
              onRemove={handleRemoveByIndex(i)}
            />
          );
        })}

        {props.totalAmount && items.length ? (
          <Alert severity={'info'}>
            Total invoices amount &nbsp;
            <AmountFormatter {...props.totalAmount} />
          </Alert>
        ) : null}
      </Stack>
    </Section>
  );
};

export default Invoices;
